import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ForecastRow, HeatmapData, InventoryValuationData, KpiData, LogisticsRow, MetricData, MetricRequest, MovementRow, ProductItem, PurchaseRow, RankItem, UrgencyKpiData, Visualization, WidgetInstance } from '@contracts';
import { METRIC_CATALOG } from '@contracts';
import { fetchMetric } from '@/services/metricService';
import { fetchCustomMetric } from '@/services/customMetricsService';
import { useFilters } from '@/lib/filterContext';
import { useDrilldown } from '@/lib/drilldownContext';
import { PER_COMPANY_METRIC } from '@/lib/companyVisibility';
import { isDashboardAdmin } from '@/services/authService';
// echarts es el chunk más pesado (~1.5MB) → fuera del bundle inicial; se carga
// solo cuando un widget realmente renderiza una gráfica.
const ChartWidget = lazy(() => import('./ChartWidget').then((m) => ({ default: m.ChartWidget })));
import { TableWidget } from './TableWidget';
import { RankListWidget } from './RankListWidget';
import { HeatmapWidget } from './HeatmapWidget';
import { LogisticsTableWidget } from './LogisticsTableWidget';
import { PurchasesTableWidget } from './PurchasesTableWidget';
import { MovementsTableWidget } from './MovementsTableWidget';
import { ForecastTableWidget } from './ForecastTableWidget';
import { RedistributionTableWidget } from './RedistributionTableWidget';
import { ProductsSplitWidget } from './ProductsSplitWidget';
import { kpiTheme } from './kpiTheme';
import { CountUp } from '@/components/CountUp';
import { InfoPopover } from '@/components/InfoPopover';

// Subtítulo + tooltip por KPI (como el dashboard original).
const KPI_SUBTITLE: Record<string, string> = {
  sales: 'neto del periodo',
  orders: 'órdenes en el período',
  products: 'unidades totales',
  avgTicket: 'por orden',
  newClients: 'en el período',
  clientsNew: 'nuevos en el período',
  clientsCabin: 'cabina en el período',
  layaways: 'abonado del periodo',
  layawaysVg: 'abonado Vine Garden',
  layawaysSb: 'abonado SmartBamboo',
  salesVg: 'ventas Vine Garden',
  salesPi: 'ventas Pielé',
  salesSb: 'ventas SmartBamboo',
  sbNewClients: 'altas en el período',
  sbClientsOro: 'compraron target',
  sbClientsPlata: 'solo productos chicos',
};
// Pie del popover de info: invita al drill-down en las KPI cards clickeables.
const KPI_HINT: Record<string, string> = {
  sales: 'Clic en la card para ver todas las órdenes.',
  avgTicket: 'Clic en la card para ver todas las órdenes.',
  orders: 'Clic en la card para ver el detalle de cada pago.',
  products: 'Clic en la card para ver el desglose por producto.',
  newClients: 'Clic en la card para ver la lista de clientes.',
  clientsNew: 'Clic en la card para ver los clientes nuevos.',
  clientsCabin: 'Clic en la card para ver los clientes de cabina.',
  layaways: 'Clic en la card para ver los apartados.',
  layawaysVg: 'Clic en la card para ver los apartados de Vine Garden.',
  layawaysSb: 'Clic en la card para ver los apartados de SmartBamboo.',
  sbNewClients: 'Clic en la card para ver la lista de altas.',
  sbClientsOro: 'Clic en la card para ver los clientes Oro.',
  sbClientsPlata: 'Clic en la card para ver los clientes Plata.',
};
// Widget custom (metric=null) → métrica del catálogo equivalente según su MODELO, para que
// conserve TEMA (color) y DRILL. Espejo de CATALOG_AS_CUSTOM. Recupera incluso widgets ya
// guardados con metric=null (sin re-guardar). Modelos ambiguos (pos.order) → su métrica base.
const MODEL_TO_METRIC: Record<string, string> = {
  'pos.wishlist': 'layaways',
  'res.partner': 'newClients',
  'pos.order': 'sales',
  'pos.payment': 'orders',
  'product.template': 'products',
};
// Regla maestra (en palabras simples para el usuario). Solo en métricas de venta.
const GOLDEN_RULE = 'Las ventas devueltas no cuentan en el total. En el detalle aparecen marcadas, pero no suman.';

// Info de cada widget: qué muestra (claro y conciso) + si aplica la regla maestra.
const WIDGET_INFO: Record<string, { desc: string; gold?: boolean }> = {
  sales: { desc: 'Ventas netas del período (lo vendido, menos las devoluciones).', gold: true },
  orders: { desc: 'Número de transacciones (órdenes) del período.', gold: true },
  products: { desc: 'Unidades de producto vendidas en el período.', gold: true },
  avgTicket: { desc: 'Ticket promedio = ventas netas ÷ número de órdenes reales.', gold: true },
  newClients: { desc: 'Altas de clientes en el período (por fecha de registro). No es una venta, así que no se le restan devoluciones.' },
  clientsNew: { desc: 'Clientes NUEVOS: registrados en el período con correo, teléfono, equipo de venta y agregado por, y con 1 o 0 órdenes en toda su historia (aún no pasan a cabina).' },
  clientsCabin: { desc: 'Clientes CABINA: registrados en el período con más de 1 venta en toda su historia y con un plan de tratamiento (abierto o no).' },
  layaways: { desc: 'Apartados: lo abonado en el período, lo que falta por cobrar y el total.' },
  layawaysVg: { desc: 'Apartados de Vine Garden / Pielé: lo abonado en el período, lo que falta por cobrar y el total.' },
  layawaysSb: { desc: 'Apartados de SmartBamboo (Anticipos): lo abonado en el período, por cobrar y total.' },
  salesVg: { desc: 'Total de ventas solo de Vine Garden en el período.', gold: true },
  salesPi: { desc: 'Total de ventas solo de Pielé en el período.', gold: true },
  salesSb: { desc: 'Total de ventas solo de SmartBamboo en el período.' },
  sellersRanking: { desc: 'Comisión acreditada por vendedor (la venta que se le acredita). En SmartBamboo es el reporte oficial de comisión; la comisión cuenta aunque la venta se devuelva.' },
  branchesRanking: { desc: 'En “Ventas”: ventas por sucursal. En “Por Vendedor”: comisión acreditada por sucursal.', gold: true },
  paymentMix: { desc: 'Cómo se reparten las ventas entre los métodos de pago.', gold: true },
  dailyTrend: { desc: 'Ventas día por día del período.', gold: true },
  hourlyHeatmap: { desc: 'Ventas por día de la semana y hora — para identificar las horas pico.', gold: true },
  productsTop: { desc: 'Productos más vendidos del período.', gold: true },
  productsBottom: { desc: 'Productos menos vendidos del período.', gold: true },
  productsBreakdown: { desc: 'Productos vendidos: los más y los menos vendidos del período.', gold: true },
  productsAlmohadas: { desc: 'Almohadas vendidas (SmartBamboo).', gold: true },
  productsTarjets: { desc: 'Colchón · Bases · Silla vendidos (SmartBamboo).', gold: true },
  productsRopaCama: { desc: 'Ropa de cama vendida (SmartBamboo).', gold: true },
  sbTopPlazas: { desc: 'Las 3 sucursales con más ventas (SmartBamboo).', gold: true },
  sbTopGerentes: { desc: 'Los 3 Gerentes de Tienda con más comisión acreditada (SmartBamboo).' },
  sbTopVendedores: { desc: 'Los 3 Vendedores con más comisión acreditada (SmartBamboo).' },
  sbNewClients: { desc: 'Altas de clientes (SmartBamboo): clientes que compraron en SB en el período y se registraron en ese rango. Deduplicados.' },
  sbClientsOro: { desc: 'Clientes Oro (SmartBamboo): compraron algún producto target (Colchón / Bases / Silla) en el período.' },
  sbClientsPlata: { desc: 'Clientes Plata (SmartBamboo): compraron solo productos chicos (no-target) en el período. Excluyente con Oro.' },
};

/** Ayuda EDITABLE del widget (override por-widget `config.help`, si no el default de la métrica). */
function helpRawFor(config: WidgetInstance['config']): string {
  return config.help ?? (config.metric ? WIDGET_INFO[config.metric]?.desc : undefined) ?? '';
}

/** Texto completo del popover: ayuda (override o default) + regla maestra (si aplica la métrica). */
function widgetInfo(config: WidgetInstance['config']): string | undefined {
  const help = config.help ?? (config.metric ? WIDGET_INFO[config.metric]?.desc : undefined);
  if (!help) return undefined;
  const gold = config.metric ? WIDGET_INFO[config.metric]?.gold : false;
  return gold ? `${help}\n\n${GOLDEN_RULE}` : help;
}

export function WidgetCard({
  widget,
  editMode,
  onEdit,
  onRemove,
  onResetConfig,
  canReset,
  onSaveHelp,
}: {
  widget: WidgetInstance;
  editMode?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  /** Restaura la config interna del widget a su default (solo widgets de preset). */
  onResetConfig?: () => void;
  canReset?: boolean;
  /** Guardar la ayuda editada del widget (lo cablea DashboardPage al scope). Solo admin. */
  onSaveHelp?: (id: string, help: string) => void;
}) {
  const { config } = widget;
  // Edición de la ayuda del popover ⓘ: solo admin (gerente/regional no ven el lápiz).
  const onSaveHelpFn = onSaveHelp && isDashboardAdmin() ? (t: string) => onSaveHelp(widget.id, t) : undefined;
  // editFirst en modo edición: clic en ⓘ abre directo el editor del texto (puntos 1-2 del UX).
  const helpEdit = onSaveHelpFn
    ? { editValue: helpRawFor(config), onSaveHelp: onSaveHelpFn, editFirst: !!editMode }
    : undefined;
  const { period, companies, scope, view } = useFilters();
  const { openOrders, openPayments, openProducts, openClients, openLayaways, openCustom } = useDrilldown();
  const entry = METRIC_CATALOG.find((m) => m.key === config.metric);
  const custom = config.customQuery;
  // Un custom puede pinnearse a compañías (ej. Apartados VG). Si las trae, mandan sobre el
  // filtro global; si no, sigue el filtro (comportamiento default).
  const effCompanies = custom?.companies?.length ? custom.companies : companies;
  // Métrica efectiva para TEMA y DRILL: la del catálogo si la hay; si es custom, la equivalente
  // por su modelo. Así un widget personalizado conserva color y clic-al-detalle.
  const drillMetric = config.metric ?? (custom ? MODEL_TO_METRIC[custom.model] : undefined);
  const title = config.title ?? entry?.label ?? (custom ? 'Métrica personalizada' : 'Widget');

  // Gateo por fuente: una métrica solo aplica si la empresa activa coincide con su fuente.
  const sbOnly = companies.length > 0 && companies.every((c) => c === 'sb');
  const sbActive = companies.length === 0 || companies.includes('sb');
  const sbOnlyMetric = !!entry && entry.sources.includes('sb') && !entry.sources.includes('odoo');
  const unavailable =
    !custom && !!entry && ((!entry.sources.includes('sb') && sbOnly) || (sbOnlyMetric && !sbActive));

  // Métricas-lista paginadas: el widget mantiene su página local.
  const PAGINATED = new Set(['stockMovements', 'replenishmentList', 'purchasesList']);
  const isPaginated = !!config.metric && PAGINATED.has(config.metric);
  const [page, setPage] = useState(1);
  // Al cambiar filtros (periodo/companies/scope) volver a la página 1.
  useEffect(() => { setPage(1); }, [period, companies, scope, config.metric]);

  // La vista global (Ventas | Por Vendedor) manda; config.view es el default del preset.
  const req: MetricRequest | null = !custom && config.metric
    ? { metric: config.metric, view, period, comparePeriod: config.comparePeriod, companies, scope,
        ...(isPaginated ? { page, perPage: 50 } : {}) }
    : null;

  // TODOS los widgets (catálogo y custom) se rigen por el filtro de fechas global.
  const { data, isLoading, isError } = useQuery({
    queryKey: custom ? ['custom', custom, period, effCompanies] : ['metric', req],
    queryFn: () =>
      custom ? fetchCustomMetric(custom, period, effCompanies) : fetchMetric(req as MetricRequest),
    enabled: !unavailable && (custom != null || req !== null),
  });

  const pagination = (data as { meta?: { pagination?: import('@contracts').Pagination } } | undefined)?.meta?.pagination;

  // ── Drill-down: Ventas/Ticket → órdenes; Transacciones → pagos; Productos → piezas ──
  const onKpiClick =
    editMode
      ? undefined
      : custom
        ? () => openCustom(custom, title)
      : drillMetric === 'orders'
        ? () => openPayments('Transacciones / Pagos')
        : drillMetric === 'products'
          ? () => openProducts('Productos vendidos')
          : drillMetric === 'newClients'
            ? () => openClients('Clientes Registrados')
            : drillMetric === 'clientsNew'
            ? () => openClients('Clientes Nuevos', 'new')
            : drillMetric === 'clientsCabin'
            ? () => openClients('Clientes Cabina', 'cabin')
            : drillMetric === 'sbNewClients'
            ? () => openClients('Altas de clientes — SmartBamboo', 'sbNew')
            : drillMetric === 'sbClientsOro'
            ? () => openClients('Clientes Oro — SmartBamboo', 'sbOro')
            : drillMetric === 'sbClientsPlata'
            ? () => openClients('Clientes Plata — SmartBamboo', 'sbPlata')
            : drillMetric === 'layaways'
              ? () => openLayaways(title)
            : drillMetric === 'layawaysVg'
              ? () => openLayaways(title, 'odoo')
            : drillMetric === 'layawaysSb'
              ? () => openLayaways(title, 'sb')
              : drillMetric === 'sales' || drillMetric === 'avgTicket'
            ? () =>
                openOrders({
                  title,
                  subtitle: 'Órdenes del período',
                  showCommission: view === 'vendedor',
                  metricKind: drillMetric as 'sales' | 'avgTicket',
                })
            : drillMetric && PER_COMPANY_METRIC[drillMetric]
            ? () =>
                openOrders({
                  title,
                  subtitle: 'Órdenes del período',
                  metricKind: 'sales',
                  companies: [PER_COMPANY_METRIC[drillMetric as string]],
                })
            : undefined;
  // En SmartBamboo los rankings usan el NOMBRE como id (no hay id numérico). Si
  // `id` no es numérico → drill SB por nombre (comisionista / sucursal).
  const onRowDrill =
    config.metric === 'sellersRanking'
      ? (item: RankItem) => {
          const n = Number(item.id);
          openOrders({
            title: item.name,
            subtitle: 'Órdenes del período',
            showCommission: true,
            ...(Number.isFinite(n) ? { employeeId: n } : { sellerName: item.name }),
          });
        }
      : config.metric === 'branchesRanking'
        ? (item: RankItem) => {
            const n = Number(item.id);
            openOrders({
              title: item.name,
              subtitle: 'Órdenes del período',
              // Vista "Por Vendedor": la plaza se mide por comisión acreditada → modo comisión.
              showCommission: view === 'vendedor',
              ...(Number.isFinite(n) ? { posConfigId: n } : { sbBranchName: item.name }),
            });
          }
        : undefined;

  const editControls = editMode ? (
    <div className="no-drag flex shrink-0 gap-1">
      {canReset && (
        <button onClick={onResetConfig} title="Restaurar este widget a su configuración original" className="rounded px-1.5 hover:bg-white/20">↺</button>
      )}
      <button onClick={onEdit} title="Editar" className="rounded px-1.5 hover:bg-white/20">✎</button>
      <button onClick={onRemove} title="Quitar" className="rounded px-1.5 hover:bg-white/20">✕</button>
    </div>
  ) : null;

  if (unavailable) {
    return (
      <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between">
          <span className="truncate text-sm font-semibold text-slate-500">{title}</span>
          <span className="text-slate-400">{editControls}</span>
        </div>
        <div className="grid flex-1 place-items-center px-2 text-center text-xs text-slate-400">
          No disponible para Smart Bamboo
        </div>
      </div>
    );
  }

  // ── Productos Vendidos partido (Más / Menos vendidos): dos donuts ─────────
  if (config.visualization === 'split' && config.metric) {
    return (
      <ProductsSplitWidget
        base={{ view, period, companies, scope, comparePeriod: config.comparePeriod }}
        metric={config.metric}
        title={config.title ?? entry?.label ?? 'Productos Vendidos'}
        info={widgetInfo(config)}
        helpEdit={helpEdit}
        editMode={editMode}
        editControls={editControls}
      />
    );
  }

  // ── KPI: card de gradiente (estilo Salo & Gaash) ──────────────────────────
  if (config.visualization === 'kpi') {
    const theme = kpiTheme(drillMetric);
    const kpi = data?.data as KpiData | undefined;
    const info = widgetInfo(config);
    // Valuación de inventario: subtítulo dinámico (unidades · ubicaciones) desde el dato.
    const inv = config.metric === 'inventoryValuation' ? (kpi as InventoryValuationData | undefined) : undefined;
    // Urgencia de resurtido: subtítulo con desglose (esta semana · evaluados).
    const urg = config.metric === 'urgencyKpis' ? (kpi as UrgencyKpiData | undefined) : undefined;
    const subtitle = inv
      ? `${Math.round(inv.units ?? 0).toLocaleString('es-MX')} u · ${inv.locations ?? 0} ubicaciones`
      : urg
        ? `${urg.pedirSemana ?? 0} esta semana · ${urg.evaluated ?? 0} evaluados`
        : config.metric
          ? KPI_SUBTITLE[config.metric]
          : undefined;
    return (
      <div
        onClick={onKpiClick}
        title={onKpiClick ? 'Ver órdenes del período' : undefined}
        className={`relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br ${theme.gradient} p-4 text-white shadow-[0_12px_28px_-8px_rgba(0,0,0,0.45)] transition-shadow hover:shadow-[0_18px_40px_-8px_rgba(0,0,0,0.5)] ${editMode ? 'cursor-move ring-2 ring-white/40' : onKpiClick ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/85">
            {title}
            {!editMode && info && (
              <InfoPopover
                title={title}
                body={info}
                tone="light"
                hint={
                  onKpiClick
                    ? custom
                      ? 'Clic en la card para ver el detalle (los registros que componen el número).'
                      : drillMetric
                        ? KPI_HINT[drillMetric]
                        : undefined
                    : undefined
                }
                {...helpEdit}
              />
            )}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {editMode && info && <InfoPopover title={title} body={info} tone="light" {...helpEdit} />}
            {editMode && editControls}
            <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/30 bg-white/20 backdrop-blur-sm">
              <theme.Icon className="h-5 w-5" strokeWidth={2.5} />
            </div>
          </div>
        </div>
        <div className="relative z-10">
          {isLoading ? (
            <div className="text-2xl font-black opacity-60">…</div>
          ) : isError ? (
            <div className="text-sm text-white/80">Error</div>
          ) : kpi ? (
            <>
              <div className="text-3xl font-black tabular-nums leading-tight">
                <CountUp value={kpi.value} format={(n) => formatKpiNum(n, kpi.unit)} />
              </div>
              {kpi.deltaPct != null && (
                <div className="mt-0.5 text-xs font-semibold text-white/85">
                  {kpi.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(kpi.deltaPct * 100).toFixed(1)}%
                </div>
              )}
              {kpi.gross != null || kpi.refunds != null ? (
                <div className="mt-1 text-[11px] font-medium text-white/80">
                  {kpi.gross != null && (
                    <>
                      {kpi.grossLabel ?? 'Bruto'} {formatKpiNum(kpi.gross, 'MXN')}
                    </>
                  )}
                  {kpi.gross != null && kpi.refunds != null && ' · '}
                  {kpi.refunds != null && (
                    <>
                      {kpi.breakdownLabel ?? 'Devol.'} {formatKpiNum(kpi.refunds, 'MXN')}
                    </>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-white/70">{subtitle ?? kpi.unit ?? ''}</div>
              )}
            </>
          ) : null}
        </div>
        <Sparkline />
      </div>
    );
  }

  // ── Resto: card blanca ────────────────────────────────────────────────────
  return (
    <div
      className={`flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
        editMode ? 'cursor-move ring-2 ring-red-300' : ''
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 text-sm font-semibold text-slate-700">
          <span className="truncate">{title}</span>
          {widgetInfo(config) && (
            <InfoPopover title={title} body={widgetInfo(config)!} {...helpEdit} />
          )}
        </span>
        {editMode ? (
          <div className="no-drag flex shrink-0 gap-1 text-slate-400">
            {canReset && (
              <button onClick={onResetConfig} title="Restaurar este widget a su configuración original" className="rounded px-1.5 hover:bg-slate-100 hover:text-slate-700">↺</button>
            )}
            <button onClick={onEdit} title="Editar" className="rounded px-1.5 hover:bg-slate-100 hover:text-slate-700">✎</button>
            <button onClick={onRemove} title="Quitar" className="rounded px-1.5 hover:bg-rose-100 hover:text-rose-600">✕</button>
          </div>
        ) : (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-300">{config.metric ?? 'decorativo'}</span>
        )}
      </div>
      <div className="min-h-0 flex-1">
        {!custom && req === null ? (
          <Center>Sin datos (decorativo)</Center>
        ) : isLoading ? (
          <Center>Cargando…</Center>
        ) : isError ? (
          <Center>Error al cargar</Center>
        ) : data ? (
          renderBody(config.visualization, data.data, title, config.metric, onRowDrill)
        ) : null}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="no-drag mt-1 flex shrink-0 items-center justify-between border-t border-slate-100 pt-1.5 text-[11px] text-slate-500">
          <span>
            {pagination.total.toLocaleString('es-MX')} registros · pág. {pagination.page}/{pagination.totalPages}
          </span>
          <span className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="rounded border border-slate-200 px-2 py-0.5 font-semibold hover:bg-slate-50 disabled:opacity-40"
            >
              ‹ Ant
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="rounded border border-slate-200 px-2 py-0.5 font-semibold hover:bg-slate-50 disabled:opacity-40"
            >
              Sig ›
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

// Métricas de logística cuyas cifras son UNIDADES (no dinero).
const LOGISTICS_UNIT_METRICS = new Set<string>([
  'resupplyList', 'transfersList', 'stockByPos', 'coverageList',
  'stockoutList', 'redistributionList', 'resupplyUnitsByProduct',
]);

function renderBody(
  viz: Visualization,
  data: MetricData,
  title: string,
  metric?: string | null,
  onRowDrill?: (item: RankItem) => void,
): ReactNode {
  // Órdenes de compra: tabla dedicada (orden/proveedor/estado/monto).
  if (metric === 'purchasesList') {
    return <PurchasesTableWidget data={data as PurchaseRow[]} />;
  }
  // Movimientos de stock y transferencias: mismo feed (fecha/tipo/origen→destino).
  if (metric === 'stockMovements' || metric === 'stockTransfers') {
    return <MovementsTableWidget data={data as MovementRow[]} />;
  }
  // Forecast de resurtido (calendario completo) y solo-urgentes: misma tabla.
  if (metric === 'resupplyForecast' || metric === 'resupplyUrgent') {
    return <ForecastTableWidget data={data as ForecastRow[]} />;
  }
  // Redistribución: tabla con origen (tienda que pasa) + stock origen + cuánto pasa.
  if (metric === 'redistributionList') {
    return <RedistributionTableWidget data={data as LogisticsRow[]} />;
  }
  // Valuación de inventario por ubicación: es DINERO → gráfica/tabla normal,
  // nunca tabla de logística (aunque traiga `units`). Se decide por la métrica.
  const isValuation = metric === 'inventoryValuationByLocation';
  // Filas de logística (unidades, no dinero): detectar por sus campos propios.
  if (
    !isValuation &&
    Array.isArray(data) &&
    data[0] &&
    ('stock' in data[0] || 'units' in data[0] || 'days' in data[0] || 'from' in data[0])
  ) {
    return <LogisticsTableWidget data={data as LogisticsRow[]} />;
  }
  // Productos (ProductItem[]) en modo lista → RankListWidget con toolbar ($ + piezas).
  if (viz === 'table' && Array.isArray(data) && data[0] && 'qty' in data[0] && 'total' in data[0]) {
    return <RankListWidget data={data as ProductItem[]} title={title} variant="product" />;
  }
  if (viz === 'table') return <TableWidget data={data} />;
  if (viz === 'heatmap') return <HeatmapWidget data={data as HeatmapData} />;
  // Rankings (RankItem[]) → lista con medallas + barras + toolbar, como el original.
  if (Array.isArray(data) && data[0] && 'orders' in data[0] && 'value' in data[0]) {
    return <RankListWidget data={data as RankItem[]} title={title} onRowDrill={onRowDrill} />;
  }
  const unit = metric && LOGISTICS_UNIT_METRICS.has(metric) ? 'units' : 'money';
  return (
    <Suspense fallback={<Center>Cargando gráfica…</Center>}>
      <ChartWidget visualization={viz} data={data} unit={unit} />
    </Suspense>
  );
}

function formatKpiNum(value: number, unit?: string): string {
  return unit === 'MXN'
    ? value.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 })
    : Math.round(value).toLocaleString('es-MX');
}

// Sparkline decorativa (línea + área) — réplica del hero KPI del dashboard original.
function Sparkline() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full opacity-30"
      viewBox="0 0 200 60"
      preserveAspectRatio="none"
    >
      <path
        d="M0,50 L20,42 L40,46 L60,30 L80,34 L100,20 L120,26 L140,14 L160,22 L180,8 L200,12 L200,60 L0,60 Z"
        fill="url(#sparkFill)"
      />
      <path
        d="M0,50 L20,42 L40,46 L60,30 L80,34 L100,20 L120,26 L140,14 L160,22 L180,8 L200,12"
        stroke="rgba(255,255,255,0.7)"
        strokeWidth="2"
        fill="none"
      />
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Center({ children }: { children: ReactNode }) {
  return <div className="grid h-full place-items-center text-center text-sm text-slate-400">{children}</div>;
}
